import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import ms from 'ms';

import { type UUID } from '~/types/common';

import { queryKeysCompany } from '../queryKeys';

import { fetchCompanyAliasAddress } from './fetchCompanyAliasAddress';
import { type CompanyAliasAddressResponse } from './types';

/**
 * Generates the query options for the company alias address query so they can be shared between the useQuery hook and the prefetching.
 */
export const getCompanyAliasAddressQueryOptions = ({
  options = {},
  aliasAddressId,
}: {
  options?: Omit<
    UseQueryOptions<CompanyAliasAddressResponse>,
    'queryKey' | 'queryFn'
  >;
  aliasAddressId: UUID;
}): UseQueryOptions<CompanyAliasAddressResponse> => {
  return {
    enabled: Boolean(aliasAddressId),
    queryFn: async () => fetchCompanyAliasAddress(aliasAddressId),
    queryKey: queryKeysCompany.getAliasAddress(aliasAddressId),
    staleTime: ms('30s'),
    ...options,
  };
};

/**
 * React Query based custom hook for getting a single company alias address by UUID.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company-Alias-Addresses/operation/get_company_address_vadmin_company_alias_address__company_address_uuid__get
 */
export const useQueryCompanyAliasAddress = (
  aliasAddressId: UUID,
  options?: Omit<
    UseQueryOptions<CompanyAliasAddressResponse>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery<CompanyAliasAddressResponse>({
    ...getCompanyAliasAddressQueryOptions({
      options,
      aliasAddressId,
    }),
  });
};
